import { Avatar, Badge, Card, Divider, Group, Stack, Text, useMantineTheme, useMantineColorScheme } from "@mantine/core";

export type UserProfileVariant = 'full' | 'compact' | 'inline';

export interface UserProfileCardProps {
  avatarUrl?: string;
  name?: string;
  title?: string; // e.g., "Player"
  team?: string;
  franchise?: string;
  roles?: string[]; // e.g., ["GM", "Admin"]
  /**
   * Visual variant for the component.
   * - 'full': Card with large avatar, title, badges, roles (default)
   * - 'compact': Small card suitable for list contexts
   * - 'inline': No card wrapper, smaller avatar/name for very tight lists
   */
  variant?: UserProfileVariant;
  /**
   * Per-field display overrides. Only provided keys override defaults; others
   * fall back to the component's built-in defaults.
   * - showTitle: default true (only relevant to 'full' variant)
   * - showTeam: default true
   * - showFranchise: default true
   * - showRoles: default true (roles are hidden in 'inline' regardless for density)
   */
  displayOptions?: Partial<{
    showTitle: boolean;
    showTeam: boolean;
    showFranchise: boolean;
    showRoles: boolean;
  }>;
}

/**
 * UserProfileCard
 *
 * Displays a player's profile summary. Supports multiple layout variants for
 * different contexts and is styled with Mantine to support light/dark themes.
 */
export default function UserProfileCard({
  avatarUrl,
  name,
  title = "User",
  team,
  franchise,
  roles = [],
  variant = 'full',
  displayOptions,
}: UserProfileCardProps) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  // Merge display options with sensible defaults. Consumers can pass only the keys they want to override.
  const defaults = { showTitle: true, showTeam: true, showFranchise: true, showRoles: true } as const;
  const opts = { ...defaults, ...(displayOptions ?? {}) };

  // Badge styles tuned for contrast in dark/light.
  // To change badge colors globally, adjust primaryColor or add palettes in src/theme.ts
  type BadgeVariant = 'filled' | 'light';
  const teamBadgeProps: { variant: BadgeVariant; color?: string } = { variant: isDark ? 'filled' : 'light', color: theme.primaryColor };
  // Franchise badge color is sourced from theme tokens; change appTheme.other.franchiseColor to rebrand globally.
  const franchiseBadgeProps: { variant: BadgeVariant; color?: string } = { variant: isDark ? 'filled' : 'light', color: (theme.other as any)?.franchiseColor ?? 'terrorist' };
  const roleBadgeVariant: 'light' | 'outline' = isDark ? 'light' : 'outline';

  if (variant === 'inline') {
    // Inline version: no Card wrapper, very tight row with small avatar.
    // In inline variant, show team/franchise badges directly after the name on the same line for dense stacking.
    return (
      <Group gap={8} wrap="nowrap" align="center">
        <Avatar src={avatarUrl} size={28} radius="xl" />
        <Stack gap={0} style={{ flex: 1 }}>
          <Group gap={6} wrap="wrap" align="center">
            <Text fw={600} size="sm" lineClamp={1} style={{ maxWidth: '100%' }}>
              {name || "Unknown User"}
            </Text>
            {opts.showTeam && team && (
              <Badge size="xs" variant={teamBadgeProps.variant} color={teamBadgeProps.color}>
                {team}
              </Badge>
            )}
            {opts.showFranchise && franchise && (
              <Badge size="xs" variant={franchiseBadgeProps.variant} color={franchiseBadgeProps.color}>
                {franchise}
              </Badge>
            )}
          </Group>
          {/* Roles are intentionally omitted in inline for density, regardless of showRoles */}
        </Stack>
      </Group>
    );
  }

  if (variant === 'compact') {
    // Compact version: tighter layout for list rows, small cards, etc.
    return (
      <Card withBorder radius="md" p="sm">
        <Group gap="sm" wrap="nowrap" align="center">
          <Avatar src={avatarUrl} size={48} radius="md" />
          <Stack gap={2} style={{ flex: 1 }}>
            <Text fw={600} size="sm" lineClamp={1}>
              {name || "Unknown Player"}
            </Text>
            <Group gap={6} wrap="wrap">
              {opts.showTeam && team && (
                <Badge size="xs" variant={teamBadgeProps.variant} color={teamBadgeProps.color}>
                  {team}
                </Badge>
              )}
              {opts.showFranchise && franchise && (
                <Badge size="xs" variant={franchiseBadgeProps.variant} color={franchiseBadgeProps.color}>
                  {franchise}
                </Badge>
              )}
            </Group>
          </Stack>
        </Group>
      </Card>
    );
  }

  // Full version: richer layout for the profile page
  return (
    <Card withBorder radius="md" p="md">
      <Group align="flex-start" wrap="nowrap">
        <Avatar src={avatarUrl} size={96} radius="md" />
        <Stack gap={6} style={{ flex: 1 }}>
          {opts.showTitle && (
            <Text size="xs" tt="uppercase" fw={700} c={isDark ? 'gray.5' : 'gray.6'}>
              {title}
            </Text>
          )}
          <Text size="lg" fw={700}>
            {name || "Unknown User"}
          </Text>

          <Group gap={8} wrap="wrap">
            {opts.showTeam && team && (
              <Badge variant={teamBadgeProps.variant} color={teamBadgeProps.color}>
                Team: {team}
              </Badge>
            )}
            {opts.showFranchise && franchise && (
              <Badge variant={franchiseBadgeProps.variant} color={franchiseBadgeProps.color}>
                Franchise: {franchise}
              </Badge>
            )}
          </Group>

          {opts.showRoles && roles.length > 0 && (
            <>
              <Divider my={6} />
              <Group gap={6} wrap="wrap">
                {roles.map((r) => (
                  <Badge key={r} size="sm" variant={roleBadgeVariant}>
                    {r}
                  </Badge>
                ))}
              </Group>
            </>
          )}
        </Stack>
      </Group>
    </Card>
  );
}
